"use client";

import { DogCard } from '@/components/DogCard';
import { ContactCard } from '@/components/ContactCard';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import type { Dog } from '@/lib/types';
import { Bone, Sparkles, Heart, ShieldCheck, ArrowRight, Calendar, Info, HelpCircle, CreditCard, PawPrint } from 'lucide-react';
import Image from 'next/image';
import { Card, CardContent, CardHeader, CardFooter, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { cn } from "@/lib/utils";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import dynamic from 'next/dynamic';

const DogDetailDialog = dynamic(() => import('@/components/DogDetailDialog').then(mod => mod.DogDetailDialog), {
    ssr: false,
});
const InquiryForm = dynamic(() => import('@/components/InquiryForm').then(mod => mod.InquiryForm), {
    ssr: false,
});

const quickAnswers = [

    {
        q: "What do I need to bring to pickup?",
        a: "Just a safe carrier for the ride home. We’ll provide a go-home bag with some food, a blanket, and all their paperwork."
    },
    {
        q: "How big are the parents?",
        a: "Our puppy parents are toy-sized. Mom weighs approximately 6 lbs and Dad weighs approximately 8 lbs."
    },
    {
        q: "Can I reserve a puppy?",
        a: "Yes! You can reserve a specific puppy with a deposit. If you don't see exactly what you're looking for, feel free to reach out to us."
    }
];

export function HomeClient({
    initialAvailableDogs,
    initialSoldDogs,
}: {
    initialAvailableDogs: Dog[],
    initialSoldDogs: Dog[],
}) {
    const [dogToInquire, setDogToInquire] = useState<Dog | null>(null);
    const [selectedDog, setSelectedDog] = useState<Dog | null>(null);


    // Real-time data from Convex
    const liveDogs = useQuery(api.dogs.getPublishedDogs) as any[];

    const availableDogs = liveDogs
        ? liveDogs.filter(d => ['Available', 'Pending', 'Reserved'].includes(d.status || '') || (d.status === 'Sold' && d.isHappyFamily === false))
        : initialAvailableDogs;

    const soldDogs = liveDogs
        ? liveDogs.filter(d => d.status === 'Sold' && (d.isHappyFamily === true || d.isHappyFamily === undefined)).slice(0, 8)
        : initialSoldDogs;

    const allDogs = [...availableDogs, ...soldDogs];

    const scrollToContact = () => {
        const form = document.getElementById('contact-form');
        if (form) {
            form.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const handleHash = () => {
            const hash = window.location.hash;
            if (hash === '#contact-form') {
                scrollToContact();
                history.replaceState(null, '', ' ');
            } else if (hash.startsWith('#inquire-')) {
                const dogId = hash.replace('#inquire-', '');
                const dog = allDogs.find(d => d.id === dogId) as any;
                if (dog) {
                    setDogToInquire(dog);
                    history.replaceState(null, '', ' ');
                }
            }
        };

        handleHash();
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, [allDogs]);



    return (
        <div className="flex flex-col min-h-screen bg-background">
            <Header />
            <main className="flex-grow pt-24 md:pt-32">
                <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden mb-0 bg-soft-gradient">
                    {/* Wave Background Decoration */}
                    <div className="absolute bottom-0 left-0 right-0 h-32 opacity-30">
                        <Image src="/images/wave-decoration.png" alt="" fill className="object-cover object-bottom" />
                    </div>

                    <div className="container mx-auto px-6 max-w-7xl relative z-20">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            {/* Left: Text Content */}
                            <div className="text-left">
                                <Badge variant="secondary" className="px-5 py-2 mb-8 text-[10px] uppercase tracking-[0.2em] font-bold border-border shadow-sm gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                                    <span>Local • Loving • Home-Raised</span>
                                </Badge>
                                <h1 className="font-headline text-5xl md:text-7xl font-medium leading-[1.1] mb-6 text-foreground">
                                    Puppies raised <br />with love.
                                </h1>
                                <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-xl">
                                    We raise sweet, healthy Toy Poodle and Maltipoo puppies right here in our home in the Bay Area.
                                </p>
                                <div className="flex flex-col sm:flex-row items-start gap-4">
                                    <Button asChild size="lg" className="rounded-full px-10 h-14 text-base font-semibold shadow-lg transition-all">
                                        <Link href="#available-puppies">View Puppies</Link>
                                    </Button>
                                    <Button asChild variant="outline" size="lg" className="rounded-full px-10 h-14 text-base font-medium transition-all">
                                        <Link href="#contact-form">Get in Touch</Link>
                                    </Button>
                                </div>
                            </div>

                            {/* Right: Hero Photo Card */}
                            <div className="relative flex justify-center items-center">
                                <div className="relative w-full max-w-md aspect-square bg-card rounded-[3rem] shadow-soft overflow-hidden border-4 border-card">
                                    <Image
                                        src="/images/hero.jpg"
                                        alt="Adorable Bay Area Maltipoo puppy"
                                        fill
                                        className="object-cover"
                                        priority
                                    />
                                    {/* Floating decorative dots */}
                                    <div className="absolute top-8 right-8 w-12 h-12 bg-primary/30 rounded-full opacity-40 blur-xl" />
                                    <div className="absolute bottom-12 left-8 w-8 h-8 bg-secondary rounded-full opacity-40 blur-xl" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>



                <section id="available-puppies" className="py-32 bg-secondary/30">
                    <div className="container mx-auto px-6 max-w-7xl relative z-10">
                        <div className="text-center mb-20">
                            <h2 className="font-headline text-5xl md:text-7xl font-medium text-foreground mb-6 tracking-tight">Current <span className="text-primary font-normal">Litters</span></h2>
                            <p className="text-lg text-muted-foreground max-w-xl mx-auto font-light leading-relaxed">
                                These are the puppies currently available. Click on any photo to learn more about them.
                            </p>
                        </div>

                        {availableDogs.length > 0 ? (
                            <div className="space-y-24">
                                {/* Maltipoos Section */}
                                {availableDogs.filter(d => d.breed.toLowerCase().includes('maltipoo')).length > 0 && (
                                    <div className="space-y-12">
                                        <div className="flex items-center gap-4">
                                            <div className="h-px bg-border flex-1" />
                                            <h3 className="font-headline text-3xl md:text-4xl text-foreground px-4">Our Maltipoos</h3>
                                            <div className="h-px bg-border flex-1" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                                            {availableDogs.filter(d => d.breed.toLowerCase().includes('maltipoo')).map(dog => (
                                                <div key={dog.id} className="h-full">
                                                    <DogCard dog={dog} onViewDetails={setSelectedDog} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Poodles Section */}
                                {availableDogs.filter(d => d.breed.toLowerCase().includes('poodle') && !d.breed.toLowerCase().includes('maltipoo')).length > 0 && (
                                    <div className="space-y-12">
                                        <div className="flex items-center gap-4">
                                            <div className="h-px bg-border flex-1" />
                                            <h3 className="font-headline text-3xl md:text-4xl text-foreground px-4">Our Poodles</h3>
                                            <div className="h-px bg-border flex-1" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                                            {availableDogs.filter(d => d.breed.toLowerCase().includes('poodle') && !d.breed.toLowerCase().includes('maltipoo')).map(dog => (
                                                <div key={dog.id} className="h-full">
                                                    <DogCard dog={dog} onViewDetails={setSelectedDog} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Other Breeds (Fallback) */}
                                {availableDogs.filter(d => !d.breed.toLowerCase().includes('maltipoo') && !(['poodle', 'toy poodle', 'standard poodle'].some(x => d.breed.toLowerCase().includes(x)) && !d.breed.toLowerCase().includes('maltipoo'))).length > 0 && (
                                    <div className="space-y-12">
                                        <div className="flex items-center gap-4">
                                            <div className="h-px bg-border flex-1" />
                                            <h3 className="font-headline text-3xl md:text-4xl text-foreground px-4">Other Puppies</h3>
                                            <div className="h-px bg-border flex-1" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                                            {availableDogs.filter(d => !d.breed.toLowerCase().includes('maltipoo') && !(['poodle', 'toy poodle', 'standard poodle'].some(x => d.breed.toLowerCase().includes(x)) && !d.breed.toLowerCase().includes('maltipoo'))).map(dog => (
                                                <div key={dog.id} className="h-full">
                                                    <DogCard dog={dog} onViewDetails={setSelectedDog} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Card className="mx-auto max-w-3xl shadow-soft">
                                <CardHeader className="text-center pb-2">
                                    <div className="w-24 h-24 bg-secondary rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                                        <Bone className="h-10 w-10 text-muted-foreground" />
                                    </div>
                                    <CardTitle className="text-4xl font-headline font-medium text-foreground">No puppies currently available</CardTitle>
                                </CardHeader>
                                <CardContent className="text-center pb-8">
                                    <p className="text-muted-foreground max-w-md mx-auto font-light leading-relaxed text-lg">
                                        Check back soon or send us a message to ask about upcoming litters.
                                    </p>
                                </CardContent>
                                <CardFooter className="justify-center pb-12">
                                    <Button size="lg" className="rounded-full px-12 h-16 text-lg font-semibold shadow-soft-hover transition-all hover:scale-105" onClick={scrollToContact}>
                                        Send us a Message
                                    </Button>
                                </CardFooter>
                            </Card>
                        )}
                    </div>
                </section>

                {/* Past Litters Section */}
                {soldDogs.length > 0 && (
                    <section className="py-24 bg-background border-t border-border/50">
                        <div className="container mx-auto px-6 max-w-7xl relative z-10">
                            <div className="text-center mb-16">
                                <h2 className="font-headline text-4xl md:text-5xl font-medium text-foreground mb-4">
                                    Found <span className="text-muted-foreground font-normal">Homes</span>
                                </h2>
                                <p className="text-lg text-muted-foreground max-w-xl mx-auto font-light leading-relaxed">
                                    A few of our past puppies.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {soldDogs.map(dog => (
                                    <div key={dog.id} className="h-full opacity-90 hover:opacity-100 transition-opacity">
                                        <DogCard dog={dog} onViewDetails={setSelectedDog} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Meet Mom & Dad Vibe Section */}
                <section className="py-24 bg-secondary/20 overflow-hidden">
                    <div className="container mx-auto px-6 max-w-6xl">
                        <Card className="rounded-[4rem] border-border/50 relative overflow-hidden bg-card">
                            {/* Decorative background paw prints */}
                            <div className="absolute top-10 right-10 opacity-5 text-foreground z-0">
                                <PawPrint className="w-48 h-48" />
                            </div>

                            <CardContent className="p-12 md:p-20 relative z-10">
                                <div className="grid md:grid-cols-2 gap-16 items-center">
                                    <div>
                                        <Badge variant="secondary" className="px-4 py-2 mb-8 text-[10px] uppercase tracking-widest gap-2">
                                            <Sparkles className="w-3.5 h-3.5" />
                                            <span>Raised with Love</span>
                                        </Badge>
                                        <h2 className="font-headline text-4xl md:text-5xl font-medium text-foreground mb-8 leading-tight">
                                            Great Puppies Start with <span className="text-primary">Great Care</span>
                                        </h2>
                                        <p className="text-xl text-muted-foreground font-light leading-relaxed mb-10">
                                            We focus on raising happy, healthy puppies in a home environment. Our goal is to make sure every puppy is well-socialized and ready to be a loving family member.
                                        </p>
                                        <div className="flex flex-wrap gap-8">
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                                                    <ShieldCheck className="w-7 h-7" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-foreground">Vet Checked</p>
                                                    <p className="text-muted-foreground text-sm">Examined by a professional</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                                                    <Heart className="w-7 h-7" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-foreground">Socialized</p>
                                                    <p className="text-muted-foreground text-sm">Raised in our home</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <div className="aspect-square bg-secondary rounded-[3rem] overflow-hidden rotate-3 shadow-xl border-8 border-card">
                                            <Image src="/images/hero.jpg" alt="Parent Dog" fill className="object-cover" />
                                        </div>
                                        <div className="absolute -bottom-8 -left-8 aspect-square w-48 bg-card rounded-[2rem] overflow-hidden -rotate-6 shadow-2xl border-4 border-card hidden md:block">
                                            <Image src="/images/hero.jpg" alt="Parent Dog" fill className="object-cover" />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* FAQ Section (Added via Refactor) */}
                <section className="py-24 container mx-auto px-6 max-w-4xl">
                    <div className="text-center mb-16">
                        <h2 className="font-headline text-4xl md:text-5xl font-medium text-foreground mb-4">
                            Common <span className="text-muted-foreground font-normal">Questions</span>
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-xl mx-auto font-light">
                            Everything you need to know about bringing home your new family member.
                        </p>
                    </div>

                    <Accordion type="single" collapsible className="w-full">
                        {quickAnswers.map((item, index) => (
                            <AccordionItem key={index} value={`item-${index}`} className="border-border">
                                <AccordionTrigger className="text-lg font-medium text-foreground hover:text-primary transition-colors text-left">
                                    {item.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </section>

                {/* Embedded Contact / Inquiry Form Section */}
                <section id="contact-form" className="py-32 container mx-auto px-6">
                    <ContactCard className="max-w-3xl mx-auto" />
                </section>

            </main>
            <Footer />
            {selectedDog && (
                <DogDetailDialog
                    dog={selectedDog}
                    isOpen={!!selectedDog}
                    onOpenChange={(open) => {
                        if (!open) setSelectedDog(null);
                    }}
                />
            )}
            {dogToInquire && (
                <DogDetailDialog
                    dog={dogToInquire}
                    isOpen={!!dogToInquire}
                    onOpenChange={(open) => {
                        if (!open) setDogToInquire(null);
                    }}
                    startInInquireMode={true}
                />
            )}
        </div>
    );
}